import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import PartnersStrip from "@/components/PartnersStrip";
import EventPitchCTA from "@/components/EventPitchCTA";
import Footer from "@/components/layout/Footer";
import { Calendar, MapPin, Users, Clock, ChevronRight, Check, LayoutGrid, CalendarDays, ChevronLeft, ChevronRight as ChevronRightIcon } from "lucide-react";
import { toast } from "sonner";
import { PinButton } from "@/components/PinContext";

// Simple month-grid calendar view (portal-only).
const CalendarView = ({ events, month, setMonth }) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startDow = first.getDay(); // 0=Sun
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);

  const eventsByDay = {};
  events.forEach((e) => {
    const dt = new Date(e.date);
    if (dt.getFullYear() === month.getFullYear() && dt.getMonth() === month.getMonth()) {
      const d = dt.getDate();
      if (!eventsByDay[d]) eventsByDay[d] = [];
      eventsByDay[d].push(e);
    }
  });

  const monthLabel = month.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const prev = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const next = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));

  return (
    <div className="bg-white border-2 border-[#0A0A0A]" data-testid="events-calendar">
      <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A]">
        <button onClick={prev} className="w-9 h-9 border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#FFC2E8]" data-testid="cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="text-xl md:text-2xl font-display font-bold">{monthLabel}</h3>
        <button onClick={next} className="w-9 h-9 border-2 border-[#0A0A0A] flex items-center justify-center hover:bg-[#FFC2E8]" data-testid="cal-next">
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 border-b-2 border-[#0A0A0A]">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-[10px] font-bold uppercase tracking-widest text-[#4A4A4A] py-2 text-center border-r border-[#E5E5E5] last:border-r-0">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((d, idx) => (
          <div key={idx} className="min-h-[100px] p-2 border-r border-b border-[#E5E5E5] last:border-r-0">
            {d && (
              <>
                <div className="text-xs font-bold text-[#0A0A0A]/60 mb-1">{d}</div>
                <div className="space-y-1">
                  {(eventsByDay[d] || []).slice(0, 3).map((e) => (
                    <a key={e.event_id} href={`#event-${e.event_id}`} className="block bg-[#FF2D87] text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-1 truncate" title={e.title}>
                      {e.title}
                    </a>
                  ))}
                  {(eventsByDay[d] || []).length > 3 && (
                    <div className="text-[10px] text-[#4A4A4A]">+{eventsByDay[d].length - 3} more</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


const Events = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState("");
  const [registrations, setRegistrations] = useState({});
  const [viewMode, setViewMode] = useState("list"); // 'list' | 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const eventTags = ["Workshop", "Meetup", "Virtual", "In-Person", "Networking", "Education"];

  useEffect(() => {
    setLoading(true);
    const params = selectedTag ? `?tag=${selectedTag}` : "";
    axios.get(`${API}/events${params}`)
      .then((res) => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedTag]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/users/me/events`)
        .then((res) => {
          const regMap = {};
          res.data.forEach((e) => { regMap[e.event_id] = true; });
          setRegistrations(regMap);
        })
        .catch(() => {});
    }
  }, [user]);

  const handleRegister = async (eventId) => {
    if (!user) {
      toast.error("Please sign in to register for events");
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/register`, {});
      setRegistrations((prev) => ({ ...prev, [eventId]: true }));
      toast.success("Registered successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to register";
      toast.error(message);
    }
  };

  const handleUnregister = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/register`);
      setRegistrations((prev) => {
        const updated = { ...prev };
        delete updated[eventId];
        return updated;
      });
      toast.success("Unregistered successfully");
    } catch (error) {
      toast.error("Failed to unregister");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString("en-US", { month: "short" }),
      year: date.getFullYear(),
      time: date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
      weekday: date.toLocaleDateString("en-US", { weekday: "long" }),
    };
  };

  return (
    <div className="min-h-screen bg-white" data-testid="events-page">
      <Navbar />
      <DashboardHeader />

      {/* Section heading — slim, contained, no full-bleed hero */}
      <section className="pt-8 md:pt-10 pb-2">
        <div className="container-main">
          <p className="overline text-[#FF2D87] mb-2">Events & Workshops</p>
          <p className="text-sm md:text-base text-[#4A4A4A] max-w-2xl">
            Monthly meetups, workshops, and gatherings designed for connection, creativity, and skill-building.
          </p>
        </div>
      </section>

      {/* Filter Tags + View toggle (List ↔ Calendar) */}
      <section className="py-5 border-b border-[#E5E5E5]">
        <div className="container-main">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedTag("")}
              className={`tag ${!selectedTag ? "tag-blue" : ""}`}
              data-testid="filter-all"
            >
              All Events
            </button>
            {eventTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`tag ${selectedTag === tag ? "tag-blue" : ""}`}
                data-testid={`filter-${tag.toLowerCase()}`}
              >
                {tag}
              </button>
            ))}
          </div>

          {user && (
            <div className="mt-5 flex gap-2 items-center" data-testid="events-view-toggle">
              <span className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">View:</span>
              <button
                onClick={() => setViewMode("list")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 border-[#0A0A0A] transition-colors ${viewMode === "list" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#FFC2E8]"}`}
                data-testid="view-list-btn"
              >
                <LayoutGrid className="w-3.5 h-3.5" /> List
              </button>
              <button
                onClick={() => setViewMode("calendar")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 border-[#0A0A0A] transition-colors ${viewMode === "calendar" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#FFC2E8]"}`}
                data-testid="view-calendar-btn"
              >
                <CalendarDays className="w-3.5 h-3.5" /> Calendar
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Events List */}
      <section className="py-10 md:py-14">
        <div className="container-main">
          {loading ? (
            <div className="space-y-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="card-brutal p-8">
                  <div className="skeleton h-8 w-1/2 mb-4" />
                  <div className="skeleton h-4 w-3/4 mb-2" />
                  <div className="skeleton h-4 w-1/4" />
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-[#E5E5E5] mx-auto mb-4" />
              <p className="text-xl text-[#4A4A4A]">No upcoming events</p>
              <p className="text-[#4A4A4A] mt-2">Check back soon for new events!</p>
            </div>
          ) : viewMode === "calendar" && user ? (
            <CalendarView events={events} month={calendarMonth} setMonth={setCalendarMonth} formatDate={formatDate} />
          ) : (
            <>
              {/* Featured (next upcoming) event — magazine-style hero card */}
              {(() => {
                const featured = events[0];
                if (!featured) return null;
                const { day, month, year, time, weekday } = formatDate(featured.date);
                const isRegistered = registrations[featured.event_id];
                return (
                  <article
                    className="relative grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-[#0A0A0A] overflow-hidden mb-12 shadow-[8px_8px_0_#FF2D87]"
                    data-testid={`event-featured-${featured.event_id}`}
                  >
                    {/* Image / color block */}
                    <div className="lg:col-span-7 relative bg-[#FF2D87] min-h-[260px] lg:min-h-[420px] flex items-end overflow-hidden">
                      {featured.image_url ? (
                        <img src={featured.image_url} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Calendar className="w-32 h-32 text-white/20" />
                        </div>
                      )}
                      {/* Date badge */}
                      <div className="absolute top-5 left-5 bg-white border-2 border-[#0A0A0A] px-4 py-2 text-center shadow-[4px_4px_0_#0A0A0A]">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF2D87]">{month}</p>
                        <p className="text-3xl font-display font-bold leading-none">{day}</p>
                        <p className="font-mono text-[10px] text-[#4A4A4A]">{year}</p>
                      </div>
                      <div className="absolute top-5 right-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.3em] bg-[#0A0A0A] text-[#FF2D87] px-3 py-1.5">★ Featured · Next up</p>
                      </div>
                      {user && (
                        <div className="absolute bottom-5 right-5">
                          <PinButton targetType="event" targetId={featured.event_id} className="w-9 h-9 rounded-full" onLight />
                        </div>
                      )}
                    </div>

                    {/* Editorial right column */}
                    <div className="lg:col-span-5 p-6 md:p-8 bg-white flex flex-col">
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        {featured.tags?.slice(0, 3).map((tag) => (
                          <span key={tag} className="tag-pink text-[10px]">{tag}</span>
                        ))}
                        {featured.is_members_only && <span className="badge badge-primary text-[10px]">Members Only</span>}
                      </div>
                      <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-3 leading-tight">
                        {featured.title}
                      </h3>
                      <p className="text-[#4A4A4A] mb-5 line-clamp-3">{featured.description}</p>
                      <div className="space-y-2 text-sm text-[#0A0A0A] mb-6 font-mono">
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#FF2D87]" /> {weekday}, {time}</div>
                        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#5170FF]" /> {featured.location}</div>
                        {featured.capacity && (
                          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#0A0A0A]" /> {featured.capacity} spots</div>
                        )}
                      </div>
                      <div className="mt-auto flex items-center gap-3">
                        {featured.price > 0 && <span className="text-xl font-bold">${featured.price}</span>}
                        {isRegistered ? (
                          <button onClick={() => handleUnregister(featured.event_id)} className="btn-secondary flex items-center gap-2" data-testid={`unregister-${featured.event_id}`}>
                            <Check className="w-4 h-4" /> You're going
                          </button>
                        ) : (
                          <button onClick={() => handleRegister(featured.event_id)} className="btn-primary flex items-center gap-2" data-testid={`register-${featured.event_id}`}>
                            Save my spot <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })()}

              {/* Rest — editorial grid */}
              {events.length > 1 && (
                <>
                  <div className="flex items-baseline justify-between mb-5 border-b-2 border-[#0A0A0A] pb-2">
                    <h3 className="font-display font-bold text-xl text-[#0A0A0A]">Coming up</h3>
                    <span className="font-mono text-xs text-[#4A4A4A]">{events.length - 1} more {events.length - 1 === 1 ? "event" : "events"}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {events.slice(1).map((event) => {
                      const { day, month, time, weekday } = formatDate(event.date);
                      const isRegistered = registrations[event.event_id];
                      // Alternate accent colors for magazine variety
                      const accents = ["#5170FF", "#FF2D87", "#FFC2E8"];
                      const accent = accents[events.indexOf(event) % accents.length];
                      const textOnAccent = accent === "#FFC2E8" ? "#0A0A0A" : "white";
                      return (
                        <article
                          key={event.event_id}
                          className="group relative bg-white border-2 border-[#0A0A0A] overflow-hidden hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A] transition-all flex flex-col"
                          data-testid={`event-${event.event_id}`}
                        >
                          {/* Image strip */}
                          <div className="relative h-44 overflow-hidden" style={{ background: accent }}>
                            {event.image_url ? (
                              <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Calendar className="w-16 h-16" style={{ color: textOnAccent, opacity: 0.4 }} />
                              </div>
                            )}
                            {/* Date stamp */}
                            <div className="absolute top-3 left-3 bg-white border-2 border-[#0A0A0A] px-3 py-1.5 text-center shadow-[3px_3px_0_#0A0A0A]">
                              <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: accent === "#FFC2E8" ? "#FF2D87" : accent }}>{month}</p>
                              <p className="text-xl font-display font-bold leading-none">{day}</p>
                            </div>
                            {user && (
                              <div className="absolute top-3 right-3">
                                <PinButton targetType="event" targetId={event.event_id} className="w-8 h-8 rounded-full" onLight />
                              </div>
                            )}
                          </div>

                          <div className="p-5 flex flex-col flex-1">
                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                              {event.tags?.slice(0, 2).map((tag) => (
                                <span key={tag} className="text-[10px] font-bold uppercase tracking-widest border border-[#0A0A0A] px-2 py-0.5">{tag}</span>
                              ))}
                              {event.is_members_only && <span className="text-[10px] font-bold uppercase tracking-widest bg-[#FF2D87] text-white px-2 py-0.5">Members</span>}
                            </div>
                            <h4 className="text-lg md:text-xl font-display font-bold leading-snug mb-2 line-clamp-2">{event.title}</h4>
                            <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-4">{event.description}</p>
                            <div className="text-xs text-[#4A4A4A] font-mono space-y-1 mb-4">
                              <p className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {weekday}, {time}</p>
                              <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {event.location}</p>
                            </div>
                            <div className="mt-auto flex items-center justify-between">
                              {event.price > 0 ? (
                                <span className="font-bold">${event.price}</span>
                              ) : <span className="text-[10px] font-bold uppercase tracking-widest text-[#5170FF]">Free</span>}
                              {isRegistered ? (
                                <button onClick={() => handleUnregister(event.event_id)} className="text-xs font-bold uppercase tracking-widest text-[#5170FF] inline-flex items-center gap-1" data-testid={`unregister-${event.event_id}`}>
                                  <Check className="w-3.5 h-3.5" /> Going
                                </button>
                              ) : (
                                <button onClick={() => handleRegister(event.event_id)} className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#FF2D87] inline-flex items-center gap-1" data-testid={`register-${event.event_id}`}>
                                  Register <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA — pitch a workshop, submits to admin panel (not email) */}
      <EventPitchCTA />

      <PartnersStrip />
      <Footer />
    </div>
  );
};

export default Events;
